import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  useGetAiCredentialsQuery,
  useGetActiveAiImplementationRunQuery,
  useGetAiImplementationRunQuery,
  useCancelAiImplementationRunMutation,
  useStartAiImplementationMutation,
} from '../api/aiImplementationApi';
import { isTerminalStatus, type AiImplementationRun, type AiReadinessResult } from '../types';
import { useAiImplementationProgressStream } from '../hooks/useAiImplementationProgressStream';
import { extractErrorMessage } from '../../../utils/errorUtils';

type Props = {
  open: boolean;
  taskId: number | null;
  onClose: () => void;
};

const pipeline = [
  { stage: 'Preparing', label: 'Preparing' },
  { stage: 'Analyzing', label: 'Analyzing' },
  { stage: 'Editing', label: 'Editing' },
  { stage: 'Validating', label: 'Validating' },
  { stage: 'CommitPush', label: 'Commit / Push' },
  { stage: 'PullRequest', label: 'Pull request' },
  { stage: 'Completed', label: 'Completed' },
];

function parseReadiness(value?: string | null): AiReadinessResult | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as AiReadinessResult;
  } catch {
    return null;
  }
}

export const ImplementWithAiDialog = ({ open, taskId, onClose }: Props) => {
  const credentialsQuery = useGetAiCredentialsQuery(undefined, { skip: !open });
  const [startRun, startState] = useStartAiImplementationMutation();
  const [cancelRun, cancelState] = useCancelAiImplementationRunMutation();
  const [credentialId, setCredentialId] = useState<number | ''>('');
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const [streamedRun, setStreamedRun] = useState<AiImplementationRun | null>(null);
  const activeRunQuery = useGetActiveAiImplementationRunQuery(taskId ?? 0, {
    skip: !open || !taskId,
    refetchOnMountOrArgChange: true,
  });
  const runId = startedRunId ?? activeRunQuery.data?.runId ?? null;
  const runQuery = useGetAiImplementationRunQuery(runId ?? '', { skip: !open || !runId });

  const credentials = useMemo(() => credentialsQuery.data ?? [], [credentialsQuery.data]);
  const selectedCredentialId =
    credentialId || (credentials.find((item) => item.isDefault) ?? credentials[0])?.id || '';
  const run = streamedRun ?? runQuery.data ?? activeRunQuery.data;
  const readiness = useMemo(() => parseReadiness(run?.readinessResultJson), [run]);
  const isTerminal = Boolean(run && isTerminalStatus(run.status));
  const progressStream = useAiImplementationProgressStream({
    enabled: open && Boolean(runId) && !isTerminal,
    runId,
    onProgress: setStreamedRun,
    onTerminal: (terminalRun) => {
      setStreamedRun(terminalRun);
      void runQuery.refetch();
    },
  });

  const resetAndClose = () => {
    setStartedRunId(null);
    setStreamedRun(null);
    setCredentialId('');
    startState.reset();
    onClose();
  };

  const handleStart = async () => {
    if (!taskId || !selectedCredentialId) return;
    try {
      const created = await startRun({ taskId, credentialId: selectedCredentialId }).unwrap();
      setStartedRunId(created.runId);
      setStreamedRun(created);
    } catch {
      const active = await activeRunQuery.refetch();
      if (active.data) {
        setStartedRunId(active.data.runId);
        setStreamedRun(active.data);
      }
    }
  };

  const handleCancel = async () => {
    if (!runId) return;
    try {
      await cancelRun(runId).unwrap();
      const result = await runQuery.refetch();
      if (result.data) setStreamedRun(result.data);
    } catch {
      // Ignore, RTK query will populate cancelState.error
    }
  };

  const error = credentialsQuery.error
    ? extractErrorMessage(credentialsQuery.error, 'Failed to load AI settings.')
    : startState.error
      ? extractErrorMessage(startState.error, 'Failed to start AI implementation.')
      : runQuery.error
        ? extractErrorMessage(runQuery.error, 'Failed to load implementation status.')
        : null;

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="sm">
      <DialogTitle>Implement with AI</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {runId && progressStream.state === 'reconnecting' && (
            <Alert severity="warning">
              Progress connection was interrupted. Reconnecting automatically...
            </Alert>
          )}

          {!runId && (
            <>
              <Alert severity="warning">
                This operation uses credits from the selected AI provider account.
              </Alert>

              {credentialsQuery.isLoading ? (
                <Stack alignItems="center" py={2}>
                  <CircularProgress size={24} />
                </Stack>
              ) : credentials.length === 0 ? (
                <Alert severity="info">
                  Configure an AI provider, model, and API key in My Profile first.
                </Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="ai-credential-label">Provider and model</InputLabel>
                  <Select
                    labelId="ai-credential-label"
                    label="Provider and model"
                    value={selectedCredentialId}
                    onChange={(event) => setCredentialId(Number(event.target.value))}
                  >
                    {credentials.map((credential) => (
                      <MenuItem key={credential.id} value={credential.id}>
                        {credential.provider} · {credential.modelId}
                        {credential.keyLastFour ? ` · ••••${credential.keyLastFour}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {run && (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Status</Typography>
                <Chip
                  label={run.status}
                  color={
                    run.status === 'Succeeded'
                      ? 'success'
                      : run.status === 'Failed' || run.status === 'ValidationFailed'
                        ? 'error'
                        : 'primary'
                  }
                  size="small"
                />
                {!isTerminal && <CircularProgress size={18} />}
              </Stack>

              <Typography variant="body2">
                {run.provider} · {run.modelId}
              </Typography>

              {readiness?.progress && <Alert severity="info">{readiness.progress}</Alert>}
              <ProgressPipeline run={run} readiness={readiness} />

              {readiness?.issues?.length ? (
                <Alert severity="error">
                  <Typography fontWeight={600}>Ticket needs more information:</Typography>
                  <List dense disablePadding>
                    {readiness.issues.map((issue) => (
                      <ListItem key={issue} disableGutters>
                        • {issue}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              ) : null}

              {readiness?.recommendations?.length ? (
                <Alert severity="info">
                  <List dense disablePadding>
                    {readiness.recommendations.map((recommendation) => (
                      <ListItem key={recommendation} disableGutters>
                        • {recommendation}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              ) : null}

              {run.errorSummary && <Alert severity="error">{run.errorSummary}</Alert>}
              {run.branchName && <Typography variant="body2">Branch: {run.branchName}</Typography>}
              {run.pullRequestUrl && (
                <Link href={run.pullRequestUrl} target="_blank" rel="noopener noreferrer">
                  Open pull request
                </Link>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Close</Button>
        {runId && !isTerminal && (
          <Button color="error" disabled={cancelState.isLoading} onClick={handleCancel}>
            Cancel run
          </Button>
        )}
        {!runId && (
          <Button
            variant="contained"
            disabled={!selectedCredentialId || !taskId || startState.isLoading}
            onClick={handleStart}
          >
            {startState.isLoading ? <CircularProgress size={20} /> : 'Start implementation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const ProgressPipeline = ({
  run,
  readiness,
}: {
  run: AiImplementationRun;
  readiness: AiReadinessResult | null;
}) => {
  const fallbackStage =
    run.status === 'Succeeded'
      ? 'Completed'
      : run.status === 'Validating'
        ? 'Preparing'
        : 'Analyzing';
  const stage = readiness?.progressStage ?? fallbackStage;
  const matchedIndex = pipeline.findIndex((item) => item.stage === stage);
  const currentIndex = matchedIndex < 0 ? 0 : matchedIndex;
  const failed =
    run.status === 'Failed' || run.status === 'ValidationFailed' || run.status === 'Cancelled';

  return (
    <Stack spacing={0.75} aria-label="Implementation progress">
      {pipeline.map((item, index) => {
        const completed = run.status === 'Succeeded' || index < currentIndex;
        const current = index === currentIndex;
        return (
          <Box key={item.stage} display="flex" alignItems="center" gap={1}>
            {completed ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : current && failed ? (
              <ErrorOutlineIcon color="error" fontSize="small" />
            ) : current ? (
              <CircularProgress size={18} />
            ) : (
              <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
            )}
            <Typography
              variant="body2"
              color={completed || current ? 'text.primary' : 'text.disabled'}
              fontWeight={current ? 600 : 400}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};
